import React, { useEffect, useMemo, useState } from 'react';
import { addDays, format } from 'date-fns';
import { de } from 'date-fns/locale';
import { formatDateWithWeekday, formatDateShort, formatDate, toApiDate } from '../../lib/dateFormat';
import { useTeacherTimetable } from '../../hooks/useTeacherTimetable';
import CourseTooltip from './CourseTooltip';
import { generateTimeSlots, getTimeIntervals } from '../../lib/settings';
import type { TeacherAvailabilitySlot } from '../../lib/personen';
import { layoutOverlappingEvents, overlapLayoutToColumnStyle } from '../../lib/scheduleLayout';
import { cn } from '../../lib/utils';

export interface PreviewEvent {
  dayOfWeek: number;
  startTime: string; // HH:MM
  endTime: string; // HH:MM
  title: string;
  subtitle?: string;
  fachName?: string;
  color?: string;
  /**
   * Zeile über dem Termin, abgesetzt durch eine gestrichelte Linie — etwa
   * „Gleicher Termin wie Probestunde", solange der Slot unverändert ist.
   */
  headline?: string;
}

export interface TeacherTimetableProps {
  teacherId: number | string;
  referenceDate?: Date; // beliebiges Datum in der Woche
  selectable?: boolean;
  onSelectSlot?: (p: { date: string; dayOfWeek: number; startTime: string; endTime: string }) => void;
  onSelectEvent?: (e: TeacherTimetableSelectEvent) => void;
  height?: number;
  showLegend?: boolean;
  onWeekChange?: (weekStart: Date) => void;
  selectedSlot?: { dayOfWeek: number; startTime: string } | null;
  previewEvent?: PreviewEvent | null; // Preview-Event für Auswahl
  availabilitySlots?: TeacherAvailabilitySlot[]; // wöchentlich wiederkehrende Freigabezeiten
  displayMode?: 'auto' | 'week' | 'day';
  showNavigation?: boolean;
  showTodayButton?: boolean;
  allowOccupiedSelection?: boolean; // Probestunden dürfen auch auf belegte Zeiten gelegt werden
  /**
   * Termine, die hier nichts zu suchen haben — etwa die Probestunde, aus der
   * gerade eine Anmeldung wird. Sie stand sonst grün neben dem neuen Kurs und
   * sah aus wie ein zweiter Termin.
   */
  hideEventIds?: readonly string[];
}

export type TeacherTimetableSelectEvent = {
  id: string;
  type: 'unit' | 'trial' | 'pattern';
  courseId?: number;
  date?: string; // YYYY-MM-DD (konkreter Tag im aktuellen View)
  dayOfWeek?: number; // 1..7 (Mo=1)
  startTime?: string; // HH:MM
  endTime?: string; // HH:MM
  startsAt?: string | null; // Kursbeginn (nur Muster)
};

function addMinutesToTime(hhmm: string, minutes: number): string {
  const [hh, mm] = hhmm.split(':').map(Number);
  const total = hh * 60 + mm + minutes;
  const h2 = ((Math.floor(total / 60) % 24) + 24) % 24;
  const m2 = ((total % 60) + 60) % 60;
  return `${String(h2).padStart(2, '0')}:${String(m2).padStart(2, '0')}:00`;
}

function hhmmToMinutes(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
}

function normalizeToHHMM(t: string): string {
  if (!t) return '';
  return t.length >= 5 ? t.slice(0, 5) : t;
}

function isWithinAvailability(dayOfWeek: number, slotHHMM: string, availabilitySlots: TeacherAvailabilitySlot[] | undefined): boolean {
  if (!availabilitySlots || availabilitySlots.length === 0) return false;
  const daySlots = availabilitySlots.filter(s => Number(s.dayOfWeek) === Number(dayOfWeek));
  if (daySlots.length === 0) return false; // Freigabe gepflegt, aber nicht für diesen Tag
  const tMin = hhmmToMinutes(slotHHMM);
  return daySlots.some(s => {
    const sMin = hhmmToMinutes(normalizeToHHMM(s.time_start));
    const eMin = hhmmToMinutes(normalizeToHHMM(s.time_end));
    // Slot ist Startzeit; wir erlauben, wenn sie innerhalb [start,end) liegt
    return tMin >= sMin && tMin < eMin;
  });
}

const TeacherTimetable: React.FC<TeacherTimetableProps> = ({
  teacherId,
  referenceDate,
  selectable = true,
  onSelectSlot,
  onSelectEvent,
  height = 520,
  onWeekChange,
  selectedSlot,
  previewEvent,
  availabilitySlots,
  displayMode = 'auto',
  showNavigation = false,
  showTodayButton = false,
  allowOccupiedSelection = false,
  hideEventIds,
}) => {
  // Wichtig: wenn referenceDate nicht übergeben wird, darf es nicht bei jedem Render ein neues Date-Objekt sein
  // (sonst Endlos-Refetch/Render-Loop in useTeacherTimetable).
  const effectiveReferenceDate = useMemo(() => referenceDate ?? new Date(), [referenceDate]);
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    if (displayMode !== 'auto') return;
    const mql = globalThis.matchMedia('(max-width: 768px)');
    const apply = () => setIsMobile(!!mql.matches);
    apply();
    mql.addEventListener?.('change', apply);
    return () => mql.removeEventListener?.('change', apply);
  }, [displayMode]);

  const effectiveMode: 'week' | 'day' = displayMode === 'auto' ? (isMobile ? 'day' : 'week') : displayMode;

  const { weekStart, events, occupiedMap } = useTeacherTimetable(teacherId, effectiveReferenceDate);
  const versteckt = useMemo(() => new Set(hideEventIds || []), [hideEventIds]);

  const timeSlots = useMemo(() => generateTimeSlots(), []);
  const intervals = useMemo(() => getTimeIntervals(), []);
  const stepMinutes = useMemo(() => {
    if (intervals.length >= 2) {
      const [a, b] = intervals;
      const [ah, am] = a.split(':').map(Number);
      const [bh, bm] = b.split(':').map(Number);
      return bh * 60 + bm - (ah * 60 + am);
    }
    return 15;
  }, [intervals]);
  const weekDays = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);
  const displayedDays = useMemo(() => (effectiveMode === 'day' ? [effectiveReferenceDate] : weekDays), [effectiveMode, effectiveReferenceDate, weekDays]);
  const gridTemplateColumns = useMemo(() => `56px repeat(${displayedDays.length}, minmax(0, 1fr))`, [displayedDays.length]);

  const onPrev = () => onWeekChange?.(addDays(effectiveReferenceDate, effectiveMode === 'day' ? -1 : -7));
  const onNext = () => onWeekChange?.(addDays(effectiveReferenceDate, effectiveMode === 'day' ? 1 : 7));
  const onToday = () => onWeekChange?.(new Date());
  const cellPx = height <= 340 ? 22 : height <= 420 ? 26 : 32;
  const cellLineHeight = `${cellPx}px`;

  return (
    <div className={cn(showNavigation ? 'space-y-3' : 'flex h-full min-h-0 flex-col')}>
      {showNavigation ? (
        <div className="flex items-center justify-between gap-2">
          <button type="button" className="rounded-md border px-3 py-1 text-xs hover:bg-muted" onClick={onPrev}>
            &lt;
          </button>
          <div className="text-xs font-medium text-muted-foreground">
            {effectiveMode === 'day'
              ? formatDateWithWeekday(effectiveReferenceDate)
              : `${formatDateShort(weekStart)} – ${formatDate(addDays(weekStart, 6))}`}
          </div>
          <div className="flex items-center gap-2">
            {showTodayButton ? (
              <button type="button" className="rounded-md border px-2 py-1 text-xs hover:bg-muted" onClick={onToday}>
                Heute
              </button>
            ) : null}
            <button type="button" className="rounded-md border px-3 py-1 text-xs hover:bg-muted" onClick={onNext}>
              &gt;
            </button>
          </div>
        </div>
      ) : null}

      <div
        className={cn('rounded-lg border', !showNavigation && 'flex min-h-0 flex-1 flex-col')}
        style={showNavigation ? { height } : { height: '100%', minHeight: height }}
      >
        {/* Spaltenköpfe: nur Wochentag, ohne Datum */}
        <div className="grid shrink-0 border-b" style={{ gridTemplateColumns }}>
          <div className="p-2 text-center text-xs text-muted-foreground"></div>
          {displayedDays.map((d) => (
            <div key={toApiDate(d)} className="p-2 text-center text-xs font-medium">
              {effectiveMode === 'day' ? format(d, 'EEE', { locale: de }) : format(d, 'EEE', { locale: de })}
            </div>
          ))}
        </div>

        {/* Raster */}
        <div
          className={cn('overflow-y-auto', !showNavigation && 'min-h-0 flex-1')}
          style={showNavigation ? { height: height - 40 } : undefined}
        >
          <div
            className={cn('grid', effectiveMode === 'day' ? 'min-w-0' : 'min-w-[760px]')}
            style={{ gridTemplateColumns }}
          >
            {/* Zeitspalte links */}
            <div className="border-r bg-muted/30">
              {timeSlots.map((s) => (
                <div
                  key={s.time}
                  className="border-0 border-b border-solid flex items-center justify-center text-[11px] font-medium text-muted-foreground p-0 m-0"
                  style={{ minHeight: cellLineHeight, maxHeight: cellLineHeight, lineHeight: cellLineHeight }}
                >
                  {s.label}
                </div>
              ))}
            </div>
            {/* Tages-Spalten (eigene Overlay-Container) */}
            {displayedDays.map((d) => {
              const ymd = toApiDate(d);
              const dayOfWeek = ((d.getDay() + 6) % 7) + 1;
              // Pattern (=wöchentliche Kurse) nach Wochentag, echte Termine (units/trials) nach Datum
              const dayEvents = events.filter(ev => {
                if (versteckt.has(String(ev.id || ''))) return false;
                if (String(ev.id || '').startsWith('pattern-')) return ev.dayOfWeek === dayOfWeek;
                return ev.date === ymd;
              });
              const baseIdx = 0;
              const baseMin = hhmmToMinutes(timeSlots[0]?.time || '00:00');
              const toIndex = (hhmm: string) => {
                const norm = normalizeToHHMM(hhmm);
                const idx = timeSlots.findIndex(ts => ts.time === norm);
                if (idx >= 0) return idx;
                const tMin = hhmmToMinutes(norm);
                const approx = Math.round((tMin - baseMin) / stepMinutes);
                return Math.min(timeSlots.length - 1, Math.max(baseIdx, approx));
              };
              // Preview-Event hinzufügen, falls vorhanden und an diesem Tag
              const allEvents = [...dayEvents];
              if (previewEvent && previewEvent.dayOfWeek === dayOfWeek) {
                allEvents.push({
                  id: 'preview-enrollment',
                  type: 'preview' as unknown as (typeof dayEvents)[number]['type'],
                  dayOfWeek: previewEvent.dayOfWeek,
                  date: ymd,
                  startTime: previewEvent.startTime,
                  endTime: previewEvent.endTime,
                  color: previewEvent.color || 'bg-amber-100 border-amber-300 text-amber-800',
                  title: previewEvent.title || 'Neue Anmeldung',
                  subtitle: previewEvent.subtitle,
                  roomName: null,
                  fachName: previewEvent.fachName || null,
                  participants: previewEvent.subtitle || null,
                  hint: null,
                });
              }
              
              return (
                <div key={ymd} className="relative border-r last:border-r-0">
                  {/* Klickbare Slots */}
                  {timeSlots.map((s) => {
                    const occupied = occupiedMap.get(`${ymd} ${s.time}`) === true;
                    const outsideAvailability = !isWithinAvailability(dayOfWeek, s.time, availabilitySlots);
                    const hasAvailabilityRules = !!availabilitySlots && availabilitySlots.length > 0;
                    const outsideFreigabeSlot = hasAvailabilityRules && outsideAvailability && !occupied;
                    const slotBgClass = occupied
                      ? 'bg-rose-200/90'
                      : hasAvailabilityRules
                        ? (outsideAvailability ? 'bg-slate-300/50' : 'bg-emerald-100/60')
                        : 'bg-background';
                    // Prüfen ob Preview-Event an diesem Slot ist
                    const hasPreviewAtSlot = previewEvent && previewEvent.dayOfWeek === dayOfWeek && previewEvent.startTime.startsWith(s.time);
                    // Prüfen ob ein klickbares Event (Kurs) an diesem Slot ist
                    const hasClickableEvent = allEvents.some(ev => {
                      const evStartIdx = toIndex(ev.startTime);
                      const slotIdx = toIndex(s.time);
                      const isClickable = ev.id.startsWith('pattern-') && onSelectEvent;
                      return isClickable && slotIdx >= evStartIdx && slotIdx < toIndex(ev.endTime);
                    });
                    const isSelected = !!selectedSlot && selectedSlot.dayOfWeek === dayOfWeek && selectedSlot.startTime.startsWith(s.time) && !hasPreviewAtSlot;
                    return (
                      <button
                        key={`${ymd}-${s.time}`}
                        className={`block relative z-[1] w-full border-0 border-b border-solid p-0 m-0 ${slotBgClass} ${isSelected ? 'ring-2 ring-primary' : ''} hover:brightness-95`}
                        title={occupied
                          ? (allowOccupiedSelection ? 'Belegt (trotzdem auswählbar)' : 'Belegt')
                          : (availabilitySlots && availabilitySlots.length > 0 && outsideAvailability ? 'Außerhalb Freigabezeit' : undefined)}
                        disabled={!selectable || (!allowOccupiedSelection && occupied) || hasClickableEvent}
                        style={{
                          pointerEvents: hasClickableEvent ? 'none' : 'auto',
                          lineHeight: cellLineHeight,
                          minHeight: cellLineHeight,
                          maxHeight: cellLineHeight,
                          backgroundImage: outsideFreigabeSlot
                            ? 'repeating-linear-gradient(135deg, rgba(71,85,105,0.18) 0px, rgba(71,85,105,0.18) 4px, transparent 4px, transparent 8px)'
                            : undefined,
                        }}
                        onClick={(e) => {
                          if (!selectable || (!allowOccupiedSelection && occupied) || hasClickableEvent) return;
                          e.stopPropagation();
                          const startTime = `${s.time}:00`;
                          const endTime = addMinutesToTime(s.time, stepMinutes);
                          onSelectSlot?.({ date: ymd, dayOfWeek, startTime, endTime });
                        }}
                      />
                    );
                  })}
                  {/* Events Overlay innerhalb derselben Spalte */}
                  {layoutOverlappingEvents(allEvents.map((ev) => ({ ...ev, id: String(ev.id) }))).map(ev => {
                    const startIdx = toIndex(ev.startTime);
                    const endIdx = Math.max(startIdx + 1, toIndex(ev.endTime));
                    const top = startIdx * cellPx;
                    const heightPx = (endIdx - startIdx) * cellPx - 2; // kleiner Abstand zu Borders
                    const colStyle = overlapLayoutToColumnStyle(ev);
                    // Anzeigeformatierung: Zeit oben, dann Fach + Hinweis, Linie, Teilnehmer
                    const fachLabel = ev.fachName ? (ev.hint ? `${ev.fachName} (${ev.hint})` : ev.fachName) : (ev.hint || '');
                    const startLabel = normalizeToHHMM(String(ev.startTime));
                    const endLabel = normalizeToHHMM(String(ev.endTime));
                    const timeLabel = startLabel && endLabel ? `${startLabel}-${endLabel}` : (startLabel || endLabel);
                    const isPreview = String(ev.id).startsWith('preview-');
                    const timeWithFach = isPreview && fachLabel && timeLabel
                      ? `${timeLabel} · ${fachLabel}`
                      : timeLabel;
                    // Reservierter Kurs einer offenen Anmeldung: blockiert den Slot, ist aber
                    // (noch) kein Kurs, dem man jemanden zuordnen kann.
                    const isReserved = Boolean(ev.isPlaceholder) && ev.type === 'pattern';
                    const detailLine = isPreview
                      ? (ev.title || ev.participants || '')
                      : [isReserved ? 'Reserviert' : '', fachLabel, ev.participants].filter(Boolean).join(' · ');
                    const vorschauKopf = isPreview ? previewEvent?.headline : undefined;
                    const isClickable = ev.id.startsWith('pattern-') && onSelectEvent && !isReserved;
                    return (
                      <CourseTooltip
                        key={ev.id} 
                        course={{
                          name: ev.title || 'Termin',
                          fachName: ev.fachName || null,
                          teilnehmer: ev.participants || null,
                          raumName: ev.roomName || null,
                          startTime: ev.startTime,
                          endTime: ev.endTime,
                        }}
                        className={`absolute overflow-hidden rounded-md border px-1.5 py-1 text-[10px] shadow-sm ${isReserved ? 'border-dashed border-amber-500/80 bg-amber-50/95 text-amber-900' : ev.color} ${isClickable ? 'cursor-pointer hover:opacity-90' : ''}`}
                        style={{ top: top + 1, height: heightPx - 2, ...colStyle, pointerEvents: isClickable ? 'auto' : 'none', zIndex: isClickable ? 10 : 1 }}
                        onClick={isClickable ? (e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          const match = String(ev.id).match(/^pattern-(\d+)-/);
                          const courseId = match ? Number(match[1]) : undefined;
                          onSelectEvent?.({
                            id: String(ev.id),
                            type: 'pattern',
                            courseId: Number.isFinite(courseId) ? courseId : undefined,
                            date: ymd,
                            dayOfWeek,
                            startTime: normalizeToHHMM(String(ev.startTime)),
                            endTime: normalizeToHHMM(String(ev.endTime)),
                            startsAt: ev.startsAt ?? null,
                          });
                        } : undefined}
                      >
                        {ev.type === 'trial' ? (
                          // Überschrift, Zeit mit Fach, Name — so wie im Ticket
                          // skizziert (T-0028).
                          <>
                            <div className="font-semibold leading-tight">{ev.title}</div>
                            <div className="truncate leading-tight">
                              {[timeLabel, ev.fachName].filter(Boolean).join(' · ')}
                            </div>
                            {ev.participants ? (
                              <div className="truncate leading-tight">{ev.participants}</div>
                            ) : null}
                          </>
                        ) : isPreview ? (
                          <>
                            {vorschauKopf ? (
                              <div className="mb-1 border-b border-dotted border-current/40 pb-1 font-semibold leading-tight">
                                {vorschauKopf}
                              </div>
                            ) : null}
                            <div className="font-semibold whitespace-nowrap truncate">{timeLabel}</div>
                            {ev.subtitle ? <div className="truncate leading-tight">{ev.subtitle}</div> : null}
                            {ev.title ? <div className="truncate leading-tight">{ev.title}</div> : null}
                          </>
                        ) : (
                          <>
                            <div className="font-semibold whitespace-nowrap truncate">{timeWithFach}</div>
                            {detailLine ? <div className="truncate leading-tight">{detailLine}</div> : null}
                          </>
                        )}
                      </CourseTooltip>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeacherTimetable;


